import { useToast } from "@chakra-ui/react"
import React, { useEffect, useRef, useState } from "react"
import Select, { type SingleValue } from "react-select"
import { Colors } from "../../../theme/colors"
import { useAddToFMPData } from "./addToFMP.data.hook"

export interface onDropdownChangeValue {
  selectedFMP: {
    id: string
    name: string
  }
}
export interface SelectedFMPType {
  selectedFMP: {
    id: number
    name: string
  }
}
interface FMPDropdownInterface {
  onDropdownChange: (value: SelectedFMPType) => void
  disabled?: boolean
  loginNotRequired?: boolean
  defaultFmpId?: number
}

interface ProjectOption {
  value: number
  label: string
}

export const FMPDropDown = ({ onDropdownChange, disabled = false, loginNotRequired, defaultFmpId }: FMPDropdownInterface) => {
  const { data, loading, error } = useAddToFMPData(loginNotRequired)
  const toast = useToast()
  const [selectedFMP, setSelectedFMP] = useState<SelectedFMPType | null>(null)

  useEffect(() => {
    if (error) {
      toast({
        status: "error",
        description: typeof error === "object" ? error.message : error
      })
    }
  }, [error, toast])

  // Set default FMP selection when data loads and defaultFmpId is provided.
  // Must only ever apply once: this used to re-check `selectedFMP` on every
  // run, so as soon as the user picked a *different* project than
  // defaultFmpId, this effect fired again, saw the mismatch, and silently
  // reverted the dropdown back to defaultFmpId - the manual selection never
  // stuck. A ref-based one-time guard (instead of comparing to state)
  // prevents this effect from ever re-firing after the first successful
  // application, regardless of what the user selects afterwards.
  const hasAppliedDefaultRef = useRef(false)
  useEffect(() => {
    if (defaultFmpId && data?.viewUserFMPs && !loading && !hasAppliedDefaultRef.current) {
      const matchingFMP = data.viewUserFMPs.find(fmp => fmp?.id === defaultFmpId)
      if (matchingFMP?.id && matchingFMP?.project_name) {
        hasAppliedDefaultRef.current = true
        const selected = {
          selectedFMP: {
            id: Number(matchingFMP.id),
            name: matchingFMP.project_name
          }
        }
        setSelectedFMP(selected)
        onDropdownChange(selected)
      }
    }
  }, [data, defaultFmpId, loading, onDropdownChange])

  const options: ProjectOption[] = (data?.viewUserFMPs ?? [])
    .filter((fmp): fmp is NonNullable<typeof fmp> => Boolean(fmp?.id && fmp?.project_name))
    .map(fmp => ({ value: Number(fmp.id), label: fmp.project_name as string }))

  const selectedOption = options.find(option => option.value === selectedFMP?.selectedFMP.id) ?? null

  const handleChange = (option: SingleValue<ProjectOption>): void => {
    if (option) {
      const selected = { selectedFMP: { id: option.value, name: option.label } }
      setSelectedFMP(selected)
      onDropdownChange(selected)
    }
  }

  return (
    <Select<ProjectOption>
      isDisabled={disabled}
      isLoading={loading}
      isSearchable
      placeholder="Select a project..."
      options={options}
      value={selectedOption}
      onChange={handleChange}
      menuPortalTarget={document.body}
      theme={theme => ({
        ...theme,
        colors: {
          ...theme.colors,
          primary: Colors.Primary.c1,
          primary75: Colors.Primary.c1,
          primary50: Colors.Surface.s1,
          primary25: Colors.Surface.s1
        }
      })}
      styles={{
        container: base => ({ ...base, minWidth: "260px" }),
        control: (base, state) => ({
          ...base,
          minHeight: "36px",
          borderColor: state.isFocused ? Colors.Primary.c1 : base.borderColor,
          boxShadow: state.isFocused ? `0 0 0 1px ${Colors.Primary.c1}` : base.boxShadow
        }),
        menuPortal: base => ({ ...base, zIndex: 1400 }),
        option: (base, state) => ({
          ...base,
          backgroundColor: state.isSelected ? Colors.Primary.c1 : state.isFocused ? Colors.Surface.s1 : base.backgroundColor,
          color: state.isSelected ? "white" : base.color
        })
      }}
    />
  )
}
